import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, AlertCircle, Lightbulb, Brain, Link2, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';

interface Correlation {
  factor1: string;
  factor2: string;
  strength: number;
  direction: 'positive' | 'negative';
  insight: string;
  actionable: string;
}

interface Trend {
  metric: string;
  change: 'improving' | 'declining' | 'stable';
  percentage: number;
  context: string;
}

export default function PatternInsightsPanel({ userId }: { userId: string }) {
  const [correlations, setCorrelations] = useState<Correlation[]>([]);
  const [trends, setTrends] = useState<Trend[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    analyzePatterns();
  }, [userId]);

  const analyzePatterns = async () => {
    try {
      const { data: checkins } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', userId)
        .gte('date', format(subDays(new Date(), 30), 'yyyy-MM-dd'))
        .order('date', { ascending: false });

      if (!checkins || checkins.length < 7) {
        setIsLoading(false);
        return;
      }

      // Analyse correlations
      const foundCorrelations: Correlation[] = [];

      // Sleep quality vs mood - convert body_feeling text to numeric
      const bodyFeelingToScore = (feeling: string) => {
        const map: Record<string, number> = {
          'Energized & Vibrant': 10, 'Strong & Comfortable': 8, 'Neutral': 5,
          'Tired or Low Energy': 3, 'In Pain or Discomfort': 2
        };
        return map[feeling] || 5;
      };
      
      const sleepMoodCorr = calculateCorrelation(
        checkins.map(c => c.sleep_quality || 0),
        checkins.map(c => bodyFeelingToScore(c.body_feeling || ''))
      );
      if (Math.abs(sleepMoodCorr) > 0.5) {
        foundCorrelations.push({
          factor1: 'Sleep Quality',
          factor2: 'Emotional Wellbeing',
          strength: Math.abs(sleepMoodCorr),
          direction: sleepMoodCorr > 0 ? 'positive' : 'negative',
          insight: sleepMoodCorr > 0 
            ? 'Better sleep consistently leads to improved mood'
            : 'Poor sleep is significantly impacting your emotional state',
          actionable: 'Prioritise 7-9 hours of quality sleep to improve emotional resilience'
        });
      }

      // Exercise vs physical feeling
      const exerciseEnergyCorr = calculateCorrelation(
        checkins.map(c => c.exercise_duration || 0),
        checkins.map(c => bodyFeelingToScore(c.body_feeling || ''))
      );
      if (Math.abs(exerciseEnergyCorr) > 0.4) {
        foundCorrelations.push({
          factor1: 'Exercise',
          factor2: 'Energy Levels',
          strength: Math.abs(exerciseEnergyCorr),
          direction: exerciseEnergyCorr > 0 ? 'positive' : 'negative',
          insight: exerciseEnergyCorr > 0
            ? 'Regular movement is boosting your energy'
            : 'Over-exercising may be depleting your energy reserves',
          actionable: exerciseEnergyCorr > 0
            ? 'Maintain consistent exercise routine for sustained energy'
            : 'Consider reducing intensity and prioritising recovery'
        });
      }

      // Hydration vs mental clarity
      const hydrationClarityCorr = calculateCorrelation(
        checkins.map(c => c.water_intake || 0),
        checkins.map(c => bodyFeelingToScore(c.body_feeling || ''))
      );
      if (Math.abs(hydrationClarityCorr) > 0.4) {
        foundCorrelations.push({
          factor1: 'Hydration',
          factor2: 'Mental Clarity',
          strength: Math.abs(hydrationClarityCorr),
          direction: hydrationClarityCorr > 0 ? 'positive' : 'negative',
          insight: 'Your water intake directly impacts cognitive function',
          actionable: 'Aim for 8-10 glasses daily to maintain mental sharpness'
        });
      }

      // Alcohol vs sleep quality  
      const alcoholSleepCorr = calculateCorrelation(
        checkins.map(c => (c as any).alcohol_units_consumed || 0),
        checkins.map(c => c.sleep_quality || 0)
      );
      if (Math.abs(alcoholSleepCorr) > 0.3) {
        foundCorrelations.push({
          factor1: 'Alcohol Consumption',
          factor2: 'Sleep Quality',
          strength: Math.abs(alcoholSleepCorr),
          direction: 'negative',
          insight: 'Alcohol is disrupting your sleep architecture',
          actionable: 'Avoid alcohol 3-4 hours before bedtime for better rest'
        });
      }

      setCorrelations(foundCorrelations);

      // Analyse trends
      const recent = checkins.slice(0, 7);
      const previous = checkins.slice(7, 14);

      const analysedTrends: Trend[] = [];

      if (previous.length >= 7) {
        // Mood trend
        const recentMood = average(recent.map(c => bodyFeelingToScore(c.body_feeling || '')));
        const prevMood = average(previous.map(c => bodyFeelingToScore(c.body_feeling || '')));
        const moodChange = ((recentMood - prevMood) / prevMood) * 100;
        
        if (Math.abs(moodChange) > 10) {
          analysedTrends.push({
            metric: 'Emotional Wellbeing',
            change: moodChange > 0 ? 'improving' : 'declining',
            percentage: Math.abs(moodChange),
            context: moodChange > 0 
              ? 'Your mood has been on an upward trajectory'
              : 'Notice a decline in emotional state - consider what changed'
          });
        }

        // Energy trend - using body_feeling as proxy
        const recentEnergy = average(recent.map(c => bodyFeelingToScore(c.body_feeling || '')));
        const prevEnergy = average(previous.map(c => bodyFeelingToScore(c.body_feeling || '')));
        const energyChange = ((recentEnergy - prevEnergy) / prevEnergy) * 100;
        
        if (Math.abs(energyChange) > 15) {
          analysedTrends.push({
            metric: 'Energy Levels',
            change: energyChange > 0 ? 'improving' : 'declining',
            percentage: Math.abs(energyChange),
            context: energyChange > 0
              ? 'Vitality is increasing - keep up the good habits'
              : 'Energy dropping - check sleep, nutrition, and stress levels'
          });
        }

        // Sleep trend
        const recentSleep = average(recent.map(c => c.sleep_quality || 0));
        const prevSleep = average(previous.map(c => c.sleep_quality || 0));
        const sleepChange = ((recentSleep - prevSleep) / prevSleep) * 100;
        
        if (Math.abs(sleepChange) > 10) {
          analysedTrends.push({
            metric: 'Sleep Quality',
            change: sleepChange > 0 ? 'improving' : 'declining',
            percentage: Math.abs(sleepChange),
            context: sleepChange > 0
              ? 'Sleep quality improving - recovery is optimal'
              : 'Sleep disruption detected - evaluate bedtime routine'
          });
        }
      }

      setTrends(analysedTrends);
      setIsLoading(false);

    } catch (error) {
      console.error('Pattern analysis error:', error);
      setIsLoading(false);
    }
  };

  const calculateCorrelation = (x: number[], y: number[]): number => {
    const n = Math.min(x.length, y.length);
    if (n < 3) return 0;

    const meanX = average(x.slice(0, n));
    const meanY = average(y.slice(0, n));

    let numerator = 0;
    let denomX = 0;
    let denomY = 0;

    for (let i = 0; i < n; i++) {
      const dx = x[i] - meanX;
      const dy = y[i] - meanY;
      numerator += dx * dy;
      denomX += dx * dx;
      denomY += dy * dy;
    }

    const denom = Math.sqrt(denomX * denomY);
    return denom === 0 ? 0 : numerator / denom;
  };

  const average = (arr: number[]): number => {
    return arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  };

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-5/6"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Trends Section */}
      {trends.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Weekly Trends</h3>
          </div>
          <div className="space-y-3">
            {trends.map((trend, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-background/60 rounded-lg animate-fade-in">
                {trend.change === 'improving' ? (
                  <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
                ) : trend.change === 'declining' ? (
                  <TrendingDown className="h-5 w-5 text-destructive mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{trend.metric}</span>
                    <Badge variant={trend.change === 'improving' ? 'default' : 'secondary'}>
                      {trend.change === 'improving' ? '+' : ''}{trend.percentage.toFixed(0)}%
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{trend.context}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Correlations Section */}
      {correlations.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-accent/5 to-primary/5 border-accent/20">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="h-5 w-5 text-accent" />
            <h3 className="text-lg font-semibold">Pattern Discoveries</h3>
          </div>
          <div className="space-y-4">
            {correlations.map((corr, idx) => (
              <div key={idx} className="p-4 bg-background/60 rounded-lg border border-border/50 animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
                <div className="flex items-start gap-3 mb-3">
                  <Link2 className={`h-5 w-5 mt-0.5 ${corr.direction === 'positive' ? 'text-primary' : 'text-destructive'}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-sm">{corr.factor1}</span>
                      <span className="text-muted-foreground">↔</span>
                      <span className="font-medium text-sm">{corr.factor2}</span>
                      <Badge variant="outline" className="text-xs">
                        {(corr.strength * 100).toFixed(0)}% connection
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="pl-8 space-y-2">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">{corr.insight}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium text-foreground">{corr.actionable}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {correlations.length === 0 && trends.length === 0 && (
        <Card className="p-8 text-center">
          <Brain className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">
            Keep tracking for 7+ days to discover patterns and insights
          </p>
        </Card>
      )}
    </div>
  );
}
